// Action bar (monorail-wuj) — the legend's combat instructions and the
// cooldown state merged into one bottom-center strip. One slot per keyed
// active: icon + key + verb name always visible (the instructions half),
// a conic cooldown wedge + seconds readout while recharging, a pulse on
// ready, a glow while active, and a dimmed locked state before the power
// is picked up. Auto powers (sporulation, galvanism) stay off the bar —
// it answers "what can I press right now."

import type { Entity } from '../game/components'
import { powerLevel } from '../game/powers'
import { GLYPHS, type GameButton } from '../game/controls'
import type { DeviceKind } from '../engine/input'
import { skillIconSvg, type SkillIconId } from './skill-icons'

export interface ActionBar {
  tick(player: Entity): void
  /** Swap the rendered key glyphs to match the active device (Phase F,
   *  amoeba-input plan) — updates in place, never rebuilds slots. */
  setDevice(d: DeviceKind): void
}

interface SlotSpec {
  icon: SkillIconId
  // Resolves the rendered key label through GLYPHS — never a hardcoded string.
  id: GameButton
  name: string
  owned(player: Entity): boolean
  cooldown(player: Entity): number
  active(player: Entity): number
}

const SLOTS: SlotSpec[] = [
  {
    icon: 'dash', id: 'dash', name: 'dash',
    owned: () => true,
    cooldown: p => p.dash?.cooldownRemaining ?? 0,
    active: p => p.dash?.activeRemaining ?? 0,
  },
  {
    // Innate since A1 (monorail-g79): the equipped weapon shapes the verb,
    // so the slot is always owned. Weapon-specific naming comes with A5.
    icon: 'axopod', id: 'attack', name: 'attack',
    owned: () => true,
    cooldown: p => p.attackCooldown ?? 0,
    active: p => p.lash ? Math.max(0, p.lash.timer) : 0,
  },
  {
    icon: 'membrane-lock', id: 'shield', name: 'shield',
    owned: p => powerLevel(p, 'membrane-lock') > 0,
    cooldown: p => p.shield?.cooldownRemaining ?? 0,
    active: p => p.shield?.activeRemaining ?? 0,
  },
  {
    icon: 'hypertrophy', id: 'engulf', name: 'engulf',
    owned: p => powerLevel(p, 'hypertrophy') > 0,
    cooldown: p => p.engulf?.cooldownRemaining ?? 0,
    active: p => p.engulf?.activeRemaining ?? 0,
  },
  {
    // Town portal (B5) — innate like dash; the long cooldown is the cost.
    icon: 'portal', id: 'portal', name: 'recall',
    owned: () => true,
    cooldown: p => p.portal?.cooldownRemaining ?? 0,
    active: () => 0,
  },
]

interface SlotEls {
  root: HTMLDivElement
  wedge: HTMLDivElement
  secs: HTMLSpanElement
  key: HTMLSpanElement
  // Max cooldown learned from the value the systems set on activation, so
  // the bar never duplicates the per-level cooldown formulas.
  learnedMax: number
  prevCd: number
}

export function createActionBar(): ActionBar {
  const root = document.getElementById('action-bar')
  if (!root) return { tick: () => {}, setDevice: () => {} }

  const els: SlotEls[] = SLOTS.map(spec => {
    const slot = document.createElement('div')
    slot.className = 'action-slot'

    const icon = document.createElement('div')
    icon.className = 'action-slot-icon'
    icon.innerHTML = skillIconSvg(spec.icon)
    slot.appendChild(icon)

    const wedge = document.createElement('div')
    wedge.className = 'action-slot-cd'
    slot.appendChild(wedge)

    const secs = document.createElement('span')
    secs.className = 'action-slot-secs'
    slot.appendChild(secs)

    const key = document.createElement('span')
    key.className = 'action-slot-key'
    key.textContent = GLYPHS[spec.id].kbm
    slot.appendChild(key)

    const name = document.createElement('span')
    name.className = 'action-slot-name'
    name.textContent = spec.name
    slot.appendChild(name)

    root.appendChild(slot)
    return { root: slot, wedge, secs, key, learnedMax: 0, prevCd: 0 }
  })

  return {
    setDevice(d) {
      // Labels render once at creation — flip in place so learnedMax/prevCd
      // (and the mid-cooldown wedge) survive the device change untouched.
      for (let i = 0; i < SLOTS.length; i++) {
        els[i].key.textContent = GLYPHS[SLOTS[i].id][d]
      }
    },
    tick(player) {
      for (let i = 0; i < SLOTS.length; i++) {
        const spec = SLOTS[i]
        const el = els[i]

        if (!spec.owned(player)) {
          if (el.root.dataset.state !== 'locked') {
            el.root.dataset.state = 'locked'
            el.wedge.style.setProperty('--cd', '0')
            el.secs.textContent = ''
          }
          el.prevCd = 0
          continue
        }

        const cd = spec.cooldown(player)
        // A jump upward is the moment a system set the cooldown — that value
        // IS the current max for this level.
        if (cd > el.prevCd + 0.001) el.learnedMax = cd
        // Ready pulse on the frame the cooldown finishes.
        if (el.prevCd > 0 && cd === 0) {
          el.root.classList.remove('ready-pulse')
          void el.root.offsetWidth
          el.root.classList.add('ready-pulse')
        }
        el.prevCd = cd

        const state = spec.active(player) > 0 ? 'live' : cd > 0 ? 'cooldown' : 'ready'
        if (el.root.dataset.state !== state) el.root.dataset.state = state

        const frac = cd > 0 && el.learnedMax > 0 ? Math.min(1, cd / el.learnedMax) : 0
        el.wedge.style.setProperty('--cd', frac.toFixed(3))
        const secsText = cd > 1 ? String(Math.ceil(cd)) : ''
        if (el.secs.textContent !== secsText) el.secs.textContent = secsText
      }
    },
  }
}
